import { t as tr } from './i18n';
import { intlLocale } from './i18n';
import { lei } from './format';

/**
 * „Activitate recentă”: ce s-a întâmplat în firmă, din evenimente reale
 * (tabela `Event`). Nimic inventat: fiecare rând are în spate o acțiune făcută
 * în aplicație sau, pentru ce era înainte de jurnal, o dată salvată.
 */

export type EventKind =
  | 'rfq_created'
  | 'rfq_sent'
  | 'offer_received'
  | 'supplier_chosen'
  | 'order_issued'
  | 'supplement_issued'
  | 'order_canceled'
  | 'order_pending'
  | 'order_approved'
  | 'order_rejected'
  | 'order_completed'
  | 'delivery_in'
  | 'delivery_return'
  | 'rfq_closed'
  | 'rfq_canceled'
  | 'requisition_created'
  | 'requisition_edited'
  | 'requisition_rejected'
  | 'rfq_commented';

/** Instantaneul de afișare, scris odată cu evenimentul. */
export interface EventData {
  title?: string;
  supplier?: string;
  number?: string;
  /** lei fără TVA */
  total?: number | null;
  /** 'AAAA-LL-ZZ', ziua de pe aviz */
  date?: string;
  /** începutul comentariului, la `rfq_commented` */
  excerpt?: string;
  /** comentariul, ca evenimentul să dispară odată cu el */
  commentId?: string;
  /** 1 = livrarea a trecut-o omul de pe șantier: Achizițiile o confirmă */
  check?: number;
}

export interface ActivityEvent {
  id: string;
  kind: EventKind;
  /** ISO 8601 */
  at: string;
  /** numele celui care a făcut-o; null = necunoscut */
  actor: string | null;
  rfqId: string | null;
  orderId: string | null;
  data: EventData;
}

/** Un rând din listă: unul sau mai multe evenimente de același fel, adunate. */
export interface ActivityRow {
  key: string;
  kind: EventKind;
  at: string;
  actor: string | null;
  rfqId: string | null;
  orderId: string | null;
  text: string;
}

const q = (t?: string) => `„${t ?? tr('cerere ștearsă')}”`;
const money = (n?: number | null) => (n != null && n > 0 ? ` · ${lei(n)}` : '');

function textOf(kind: EventKind, d: EventData, many: string[]): string {
  switch (kind) {
    case 'rfq_created':
      return tr('Cerere nouă: {0}', q(d.title));
    case 'rfq_sent':
      return many.length > 1
        ? tr('{0} a plecat la {1} furnizori', q(d.title), many.length)
        : tr('{0} a plecat la {1}', q(d.title), d.supplier ?? tr('un furnizor'));
    case 'offer_received':
      return tr('Ofertă de la {0} pentru {1}', d.supplier ?? tr('un furnizor'), q(d.title));
    case 'supplier_chosen':
      return tr('Ales {0} pentru {1}{2}', d.supplier ?? 'furnizorul', q(d.title), money(d.total));
    case 'order_issued':
      return tr('Comanda {0} către {1}{2}', d.number ?? '', d.supplier ?? 'furnizor', money(d.total));
    case 'supplement_issued':
      return tr('Suplimentare {0} către {1}{2}', d.number ?? '', d.supplier ?? 'furnizor', money(d.total));
    case 'order_canceled':
      return tr('Comanda {0} a fost anulată', d.number ?? '');
    case 'order_pending':
      return tr(
        'Comanda {0} către {1} așteaptă aprobare{2}',
        d.number ?? '',
        d.supplier ?? 'furnizor',
        money(d.total),
      );
    case 'order_approved':
      return tr('Comanda {0} a fost aprobată', d.number ?? '');
    case 'order_rejected':
      return tr('Comanda {0} a fost respinsă', d.number ?? '');
    case 'order_completed':
      return tr('Comanda {0} e livrată integral', d.number ?? '');
    case 'delivery_in':
      return d.check
        ? tr(
            'Livrare trecută de pe șantier pe comanda {0} ({1}): de confirmat',
            d.number ?? '',
            d.supplier ?? 'furnizor',
          )
        : tr('Livrare pe comanda {0} ({1})', d.number ?? '', d.supplier ?? 'furnizor');
    case 'delivery_return':
      return d.check
        ? tr(
            'Retur trecut de pe șantier pe comanda {0} ({1}): de confirmat',
            d.number ?? '',
            d.supplier ?? 'furnizor',
          )
        : tr('Retur pe comanda {0} ({1})', d.number ?? '', d.supplier ?? 'furnizor');
    case 'rfq_closed':
      return tr('Cererea {0} a fost închisă', q(d.title));
    case 'rfq_canceled':
      return tr('Cererea {0} a fost anulată', q(d.title));
    case 'requisition_created':
      return tr('Necesar nou {0} de pe șantier: {1}', d.number ?? '', q(d.title));
    case 'requisition_rejected':
      return tr('Necesarul {0} a fost respins', d.number ?? '');
    case 'requisition_edited':
      return tr('Necesarul {0} {1} a fost modificat', d.number ?? '', q(d.title));
    case 'rfq_commented':
      return tr('Comentariu la {0}{1}', q(d.title), d.excerpt ? `: ${d.excerpt}` : '');
  }
}

/** Trimiterile aceleiași cereri, la câteva minute una de alta, sunt o singură acțiune. */
const SAME_ACTION_MS = 10 * 60 * 1000;

/**
 * Evenimentele, cele mai noi primele, ca rânduri de citit. Trimiterile
 * aceleiași cereri făcute deodată („Le-am trimis pe toate”) devin un rând:
 * „a plecat la 3 furnizori”.
 */
export function activityRows(events: ActivityEvent[], limit = 12): ActivityRow[] {
  const sorted = [...events].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  const rows: (ActivityRow & { suppliers: string[] })[] = [];
  for (const e of sorted) {
    const last = rows[rows.length - 1];
    if (
      last &&
      e.kind === 'rfq_sent' &&
      last.kind === 'rfq_sent' &&
      last.rfqId === e.rfqId &&
      new Date(last.at).getTime() - new Date(e.at).getTime() <= SAME_ACTION_MS
    ) {
      last.suppliers.push(e.data.supplier ?? '');
      last.text = textOf('rfq_sent', e.data, last.suppliers);
      continue;
    }
    if (rows.length >= limit) break;
    const suppliers = [e.data.supplier ?? ''];
    rows.push({
      key: e.id,
      kind: e.kind,
      at: e.at,
      actor: e.actor,
      rfqId: e.rfqId,
      orderId: e.orderId,
      text: textOf(e.kind, e.data, suppliers),
      suppliers,
    });
  }
  return rows.map(({ suppliers: _s, ...r }) => r);
}

/** „acum 12 min”, „acum 3 ore”, „ieri”, „acum 4 zile”, „12 septembrie” — față de `now`. */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  const min = Math.max(0, Math.floor((now - t) / 60_000));
  if (min < 1) return tr('chiar acum');
  if (min < 60) return tr('acum {0} min', min);
  const h = Math.floor(min / 60);
  const zi = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const zile = Math.round((zi(new Date(now)) - zi(new Date(t))) / 86_400_000);
  if (zile === 0) return h === 1 ? tr('acum o oră') : tr('acum {0} ore', h);
  if (zile === 1) return tr('ieri');
  if (zile < 7) return tr('acum {0} zile', zile);
  return new Date(t).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long' });
}
